import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  IStyles,
  Loader,
  PrimaryButton,
  pxToRem,
  pxTovW,
  deserify,
  getLocalStorage,
  useCommonServiceClientContext,
  theme,
} from '@geneo2-web/shared-ui';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import {
  CourseIndexInfo,
  SubjectChapterMiniInfo,
  TopicBasicInfo,
} from '@protos/content_management/content.common.apis_pb';
import { FAChapterSelectionCard } from '../../../FormativeAssessment/AssessmentCreate/components/FAChapterSelectionCard';
import { ChapterTopicPopup } from '../../../../../components/ChapterTopicPopup';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  setCourseIndexInfo,
  setCurrentQPGTemplateInfo,
  setQPGGeneratedTemplates,
  setQPGSelectedChapterData,
  setQPGSettingsConfig,
} from '../../../reducer/assessmentFlow.slice';
import { setToastInfo } from '../../../../Auth/reducer/auth.slice';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import { EditWarningPopup } from '../../../../../components/EditWarningPopup';
import { interactionEvent } from '../../../../Auth/auth.events';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    alignItems: 'center',
    marginTop: { xs: pxToRem(30), md: pxTovW(50) },
  },
  addMoreBox: {
    background: '#C1F8DA',
    display: 'flex',
    borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #0AA34F',
    padding: {
      xs: pxToRem(7),
      md: `${pxTovW(16)} ${pxTovW(23)}`,
      lg: `${pxTovW(13)} ${pxTovW(25)}`,
    },
    gap: { xs: pxToRem(5), md: pxTovW(16) },
    height: { xs: pxToRem(32), md: pxTovW(57) },
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
  },
};

export const QPGCreationChapterSelection = () => {
  const TEMP_CHAPTER_ADDED = false;

  const teacher_id = getLocalStorage('userId');
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');

  const { subject_id } = useParams();
  const { isOffline } = useDownloadContext();
  const { ContentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const {
    chapter_topic_data,
    qpg_selected_chapter_data,
    qpg_content,
    qpg_selected_section_index,
  } = deserify(useAppSelector((state) => state.assessment));
  const [openAddChapterPopup, setOpenAddChapterPopup] = useState(false);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const [selectedChapterList, setSelectedChapterList] = useState<
    SubjectChapterMiniInfo[]
  >(qpg_selected_chapter_data?.chapters || []);

  const [chapterToDelete, setChapterToDelete] = useState<
    SubjectChapterMiniInfo | undefined
  >(undefined);

  // --------------------------------------------------------------

  const fetchTotalChapterTopicData = async () => {
    if (!teacher_id) {
      return;
    }
    try {
      setLoading('loading');
      const chapterTopicData =
        await ContentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchCourseIndexInfo(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId:
              qpg_content?.multiSubjectSections?.[
                qpg_selected_section_index ?? 0
              ]?.subjectId,
          }
        );
      if (chapterTopicData?.data) {
        dispatch(setCourseIndexInfo(chapterTopicData.data));
      }
      setLoading('completed');
    } catch (error) {
      dispatch(
        setToastInfo({
          label: 'Error fetching Chapter Topic Data',
          variant: 'error',
          open: true,
        })
      );
      setLoading('error');
    } finally {
      setLoading('completed');
    }
  };

  // --------------------------------------------------------------

  const addChapterHandler = (chapter: SubjectChapterMiniInfo) => {
    const updatedChapters: SubjectChapterMiniInfo[] = [
      ...selectedChapterList,
      chapter,
    ];

    setSelectedChapterList([...updatedChapters]);
  };

  // --------------------------------------------------------------

  const PopupdeleteChapterHandler = (chapterInfo: SubjectChapterMiniInfo) => {
    if (!selectedChapterList) return;

    const updatedChapters: SubjectChapterMiniInfo[] = (
      selectedChapterList || []
    ).filter((ch) => ch.chapterId !== chapterInfo.chapterId);

    setSelectedChapterList([...updatedChapters]);
  };

  // --------------------------------------------------------------

  const PopupchapterEditHandler = (chapter: SubjectChapterMiniInfo) => {
    const updatedChapters: SubjectChapterMiniInfo[] = (
      selectedChapterList || []
    ).map((ch) => (ch.chapterId === chapter.chapterId ? chapter : ch));

    setSelectedChapterList([...updatedChapters]);
  };

  // --------------------------------------------------------------

  const deleteChapterHandler = (chapterInfo: SubjectChapterMiniInfo) => {
    if (!selectedChapterList) return;

    const updatedChapters: SubjectChapterMiniInfo[] = (
      selectedChapterList || []
    ).filter((ch) => ch.chapterId !== chapterInfo.chapterId);

    setSelectedChapterList([...updatedChapters]);
    dispatch(
      setQPGSelectedChapterData({
        ...qpg_selected_chapter_data,
        chapters: updatedChapters,
      })
    );
  };

  // --------------------------------------------------------------

  const chapterEditHandler = (chapter: SubjectChapterMiniInfo) => {
    const updatedChapters: SubjectChapterMiniInfo[] = (
      selectedChapterList || []
    ).map((ch) => (ch.chapterId === chapter.chapterId ? chapter : ch));

    setSelectedChapterList([...updatedChapters]);
    dispatch(
      setQPGSelectedChapterData({
        ...qpg_selected_chapter_data,
        chapters: updatedChapters,
      })
    );
  };

  // --------------------------------------------------------------

  const handleAddMoreClick = () => {
    console.log('Add More Clicked');
  };

  // --------------------------------------------------------------

  const handleAddChapterClick = () => {
    console.log('Add Chapter Clicked');
  };

  // --------------------------------------------------------------

  useEffect(() => {
    // dispatch(setQPGSettingsConfig(undefined));
    // dispatch(setCurrentQPGTemplateInfo(undefined));
    // dispatch(setQPGGeneratedTemplates(undefined));
    fetchTotalChapterTopicData();
  }, []);

  // When chapter_topic_data is available and qpg_selected_chapter_data has chapters (e.g. restored by parent after fetch), enrich with poster URLs
  useEffect(() => {
    if (
      loading === 'loading' ||
      !chapter_topic_data?.chapters?.length ||
      !qpg_selected_chapter_data?.chapters?.length
    ) {
      return;
    }
    const needsEnrichment =
      !qpg_selected_chapter_data.posterImageUrl ||
      qpg_selected_chapter_data.chapters.some(
        (ch) =>
          !ch.posterImagesUrl || ch.topics?.some((t) => !t.posterImagesUrl)
      );
    if (!needsEnrichment) return;

    const fetchedData = chapter_topic_data;
    const fetchedChaptersMap = new Map(
      fetchedData.chapters?.map((ch) => [ch.chapterId, ch]) ?? []
    );
    const enrichedChapters: SubjectChapterMiniInfo[] = (
      qpg_selected_chapter_data.chapters ?? []
    ).map((chapter) => {
      const fetchedChapter = fetchedChaptersMap.get(chapter.chapterId);
      const topicMap = new Map(
        fetchedChapter?.topics?.map((t) => [t.topicId, t]) ?? []
      );
      const enrichedTopics: TopicBasicInfo[] = (chapter.topics ?? []).map(
        (topic) =>
          new TopicBasicInfo({
            ...topic,
            posterImagesUrl:
              topic.posterImagesUrl ||
              topicMap.get(topic.topicId)?.posterImagesUrl ||
              '',
          })
      );
      return new SubjectChapterMiniInfo({
        ...chapter,
        posterImagesUrl:
          chapter.posterImagesUrl || fetchedChapter?.posterImagesUrl || '',
        topics: enrichedTopics,
      });
    });
    const updatedSelectedChapterData = new CourseIndexInfo({
      ...qpg_selected_chapter_data,
      posterImageUrl:
        qpg_selected_chapter_data.posterImageUrl ||
        fetchedData.posterImageUrl ||
        '',
      chapters: enrichedChapters,
    });
    dispatch(setQPGSelectedChapterData(updatedSelectedChapterData));
  }, [
    loading,
    JSON.stringify(chapter_topic_data),
    JSON.stringify(qpg_selected_chapter_data),
    dispatch,
  ]);

  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <Box>
      {!qpg_selected_chapter_data ||
      qpg_selected_chapter_data?.chapters.length === 0 ? (
        <AddChapterCTA
          addChapterClickHandler={async () => {
            setOpenAddChapterPopup(true);
            await interactionEvent({
              context: 'Question_paper_generator',
              name: 'ADD_CHAPTER',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          }}
        />
      ) : (
        <Box sx={styles.root}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              mt: { xs: pxToRem(32), md: pxTovW(20) },
            }}
          >
            <Typography variant="h2" sx={{ marginY: 'auto' }}>
              Your Added Chapters
            </Typography>
            <Box
              sx={{
                backgroundColor: 'transparent',
                background: '#C1F8DA',
                display: 'flex',
                borderRadius: { xs: pxToRem(6), md: pxTovW(10) },
                boxSizing: 'border-box',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                border: '1px solid #0AA34F',
                padding: {
                  xs: pxToRem(7),
                  md: `${pxTovW(16)} ${pxTovW(23)}`,
                  lg: `${pxTovW(13)} ${pxTovW(25)}`,
                },
                gap: { xs: pxToRem(5), md: pxTovW(16) },
                height: { xs: pxToRem(32), md: pxTovW(57) },
              }}
              onClick={async () => {
                setOpenAddChapterPopup(true);

                await interactionEvent({
                  context: 'Question_paper_generator',
                  name: 'ADD_MORE',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            >
              <AddIcon
                sx={{
                  color: '#fff',
                  fontSize: { xs: pxToRem(15), md: pxTovW(28) },
                  backgroundColor: '#03612D',
                  borderRadius: '50%',
                  p: '3px',
                }}
              />
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ color: '#0AA34F' }}
              >
                Add More
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: pxToRem(12), md: pxTovW(20) }, width: '100%' }}>
            <Grid
              container
              columns={12}
              rowGap={3}
              sx={{ padding: 0, width: '100%' }}
            >
              {qpg_selected_chapter_data?.chapters?.map((chapter, index) => (
                <Grid item md={4} xs={12} key={index}>
                  <Box
                    key={index}
                    sx={{
                      boxSizing: 'border-box',
                      width: '100%',
                      paddingX: { xs: pxToRem(10), md: pxTovW(10) },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FAChapterSelectionCard
                      selected
                      chapterInfo={chapter}
                      selectedChapterList={qpg_selected_chapter_data.chapters}
                      chapterEditHandler={async (chapter) => {
                        chapterEditHandler(chapter);
                        await interactionEvent({
                          context: 'Your_added_chapter',
                          name: 'EDIT',
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }}
                      deleteHandler={async () => {
                        setChapterToDelete(chapter);
                        await interactionEvent({
                          context: 'Your_added_chapter',
                          name: 'DELETE',
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }}
                      variant="medium"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
            <EditWarningPopup
              open={!!chapterToDelete}
              handleClose={() => setChapterToDelete(undefined)}
              yesClickHandler={() => {
                chapterToDelete && deleteChapterHandler(chapterToDelete);
                setChapterToDelete(undefined);
                dispatch(
                  setToastInfo({
                    label: `${chapterToDelete?.chapterTitle} deleted successfully`,
                    variant: 'info',
                    open: true,
                  })
                );
              }}
              noClickHandler={() => setChapterToDelete(undefined)}
              iconname="delete"
              text1={`Are you sure to  you want to delete ${chapterToDelete?.chapterTitle} ?`}
              text2=""
            />
          </Box>
        </Box>
      )}

      <ChapterTopicPopup
        open={openAddChapterPopup}
        handleClose={() => {
          setSelectedChapterList(qpg_selected_chapter_data?.chapters || []);
          setOpenAddChapterPopup(false);
        }}
        popupBody={
          <ChapterListComponent
            selectedChapterList={selectedChapterList}
            onSelectAllChapters={(chapters) => setSelectedChapterList(chapters)}
            addHandler={async (chapter) => {
              addChapterHandler(chapter);
              await interactionEvent({
                context: 'Question_paper_generator',
                name: 'ADD',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            deleteHandler={async (chapter) => {
              PopupdeleteChapterHandler(chapter);
              await interactionEvent({
                context: 'Your_added_chapter',
                name: 'DELETE',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            chapterEditHandler={async (chapter) => {
              PopupchapterEditHandler(chapter);
              await interactionEvent({
                context: 'Chapter',
                name: 'EDIT',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            confirmHandler={async () => {
              // dispatch(setFASelectedChapterData(ChapterList));
              if (selectedChapterList.length === 0) {
                dispatch(setQPGSelectedChapterData(undefined));
              } else {
                const sortedChapterList = selectedChapterList.sort(
                  (a, b) => a.chapterNo - b.chapterNo
                );
                dispatch(
                  setQPGSelectedChapterData({
                    ...chapter_topic_data,
                    chapters: sortedChapterList,
                  })
                );
              }
              setOpenAddChapterPopup(false);
              await interactionEvent({
                context: 'Add_chapter',
                name: 'CONFIRM',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          />
        }
      />
    </Box>
  );

  // return (
  //   <Box sx={styles.root}>
  //     {TEMP_CHAPTER_ADDED ? (
  //       <>
  //         <Box>
  //           <Box>
  //             <Typography variant="g2" fontWeight="medium">
  //               Your Added Chapters
  //             </Typography>
  //           </Box>
  //           <Button sx={styles.addMoreBox} onClick={handleAddMoreClick}>
  //             <AddCircleIcon
  //               sx={{
  //                 color: '#03612D',
  //                 fontSize: { xs: pxToRem(18), md: pxTovW(30) },
  //               }}
  //             />
  //             <Typography variant="h3" fontWeight="bold" color="secondary.main">
  //               ADD MORE
  //             </Typography>
  //           </Button>
  //           <Box></Box>
  //         </Box>
  //       </>
  //     ) : (
  //       <>
  //         <Typography variant="g2" fontWeight="medium">
  //           <span style={{ color: '#007CDC' }}>Step 1: </span>Select Chapters to
  //           Create Assessment first
  //         </Typography>

  //         <PrimaryButton
  //           sx={styles.buttonContainer}
  //           onClick={handleAddChapterClick}
  //         >
  //           <AddCircleIcon
  //             sx={{
  //               color: '#03612D',
  //               fontSize: { xs: pxToRem(20), md: pxTovW(40) },
  //               // backgroundColor: 'white',
  //               borderRadius: '50%',
  //               border: 'none',
  //               width: 'max-content',
  //             }}
  //           />

  //           <Typography variant="h3" color="common.white" fontWeight="bold">
  //             ADD CHAPTER
  //           </Typography>
  //         </PrimaryButton>
  //       </>
  //     )}
  //   </Box>
  // );
};

// ==============================================================

interface IPopupBodyProps {
  confirmHandler: () => void;
  addHandler: (chapter: SubjectChapterMiniInfo) => void;
  deleteHandler?: (chapter: SubjectChapterMiniInfo) => void;
  chapterEditHandler: (chapter: SubjectChapterMiniInfo) => void;
  selectedChapterList: SubjectChapterMiniInfo[];
  onSelectAllChapters?: (chapters: SubjectChapterMiniInfo[]) => void;
}
export const ChapterListComponent = (props: IPopupBodyProps) => {
  const {
    confirmHandler,
    addHandler,
    deleteHandler,
    chapterEditHandler,
    selectedChapterList,
    onSelectAllChapters,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { chapter_topic_data } = deserify(
    useAppSelector((state) => state.assessment)
  );

  const allChapters = chapter_topic_data?.chapters ?? [];
  const allChaptersSelected =
    allChapters.length > 0 &&
    allChapters.every((ch) =>
      selectedChapterList.some((s) => s.chapterId === ch.chapterId)
    );
  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          height: 'max-content',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h3" fontWeight="bold">
          Add Chapter
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: pxToRem(10), md: pxTovW(25) },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: pxToRem(5), md: pxTovW(10) },
            }}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon sx={{ color: '#007CDC' }} />}
              checkedIcon={<CheckBoxIcon sx={{ color: 'primary.main' }} />}
              checked={allChaptersSelected}
              onChange={(_, checked) => {
                onSelectAllChapters?.(checked ? [...allChapters] : []);
              }}
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: pxToRem(20), md: pxTovW(24) },
                },
                padding: 0,
              }}
            />
            <Typography variant="h4" sx={{ fontFamily: 'Poppins' }}>
              Select All
            </Typography>
          </Box>
          <PrimaryButton
            onClick={confirmHandler}
            sx={{
              width: { xs: pxToRem(104), md: pxTovW(183), lg: pxTovW(153) },
              height: { xs: pxToRem(46), md: pxTovW(63), lg: pxTovW(53) },
            }}
          >
            <Typography
              variant={isMobile ? 'h4' : 'h5'}
              color="common.white"
              fontWeight="bold"
            >
              CONFIRM
            </Typography>
          </PrimaryButton>
        </Box>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px solid #E0DFDE',
          marginY: { xs: pxToRem(10), md: pxTovW(20) },
        }}
      />
      <Box
        sx={{
          width: { xs: '100%', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          // justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: pxToRem(10), md: pxTovW(20) },
          height: '90%',
          overflowY: 'auto',
        }}
      >
        {chapter_topic_data?.chapters?.map((chapter, index) => (
          <Box key={index} sx={{ width: '100%' }}>
            <FAChapterSelectionCard
              selectedChapterList={selectedChapterList}
              selected={
                selectedChapterList
                  ? selectedChapterList.find(
                      (e) => e.chapterId === chapter.chapterId
                    ) !== undefined
                  : false
              }
              chapterInfo={chapter}
              variant="medium"
              addHandler={addHandler}
              deleteHandler={deleteHandler}
              chapterEditHandler={chapterEditHandler}
              allowCardClickDelete={true}
            />
          </Box>
        ))}
      </Box>
    </>
  );
};

// ==============================================================

interface IAddChapterProps {
  addChapterClickHandler: () => void;
}
const AddChapterCTA = (props: IAddChapterProps) => {
  const { addChapterClickHandler } = props;
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: pxToRem(20), md: pxTovW(20) },
        mb: { xs: pxToRem(20), md: pxTovW(20) },
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          marginY: { xs: pxToRem(23), md: pxTovW(41) },
        }}
      >
        <Typography variant="h2">
          <span style={{ color: '#007CDC' }}>Step 1: </span> Add Chapters and
          Topics first to create question paper
        </Typography>
      </Box>

      <PrimaryButton
        sx={{
          width: { xs: pxToRem(211), md: pxTovW(397) },
          height: { xs: pxToRem(48), md: pxTovW(77) },
          gap: { xs: pxToRem(10), md: pxTovW(10) },
        }}
        onClick={addChapterClickHandler}
      >
        <AddIcon
          sx={{
            color: '#fff',
            fontSize: { xs: pxToRem(20), md: pxTovW(35) },
            backgroundColor: '#03612D',
            borderRadius: '50%',
            p: '5px',
          }}
        />
        <Typography
          variant="h2"
          fontWeight="bold"
          color="white"
          sx={{
            fontSize: { xs: pxToRem(18), md: pxTovW(30), lg: pxTovW(24) },
          }}
        >
          ADD CHAPTER
        </Typography>
      </PrimaryButton>
    </Box>
  );
};

// ==============================================================
